import React, { useState } from "react";
import { Modal, Spinner, Button, Select } from "@/shared";
import {
  useGetLegTasksQuery,
  useGetJobTasksQuery,
  useCreateLegTaskMutation,
  useDeleteLegTaskMutation,
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useGetTaskTemplatesQuery,
  useCreateTaskTemplateMutation,
  useApplyTemplatesToLegMutation,
  useApplyTemplatesToJobMutation,
  useGetTaskTemplateGroupsQuery,
  useApplyTemplateGroupToLegMutation,
  useApplyTemplateGroupToJobMutation,
} from "@/redux/services/tasks/tasksApi";
import {
  PlusIcon,
  TrashIcon,
  CameraIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  SparklesIcon,
  ExclamationCircleIcon,
  CheckIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { ClipboardDocumentCheckIcon } from "@heroicons/react/24/solid";
import { useConfig } from "@/hooks";
import { toast } from "react-toastify";
import { usePermissions } from "@/hooks/usePermissions";

interface Task {
  _id: string;
  title: string;
  description?: string;
  completed: boolean;
  category: string;
  required: boolean;
  requiresPhoto: boolean;
  requiresSignature: boolean;
  requiresNote: boolean;
}

interface TaskTemplate {
  _id: string;
  name: string;
  title?: string;
  description?: string;
  appliesTo?: "job" | "leg" | "both";
  appType?: "trucking" | "serviceRepair" | "all";
  industry?: "trucking" | "service_repair" | "general";
  category: string;
  required: boolean;
  requiresPhoto: boolean;
  requiresSignature: boolean;
  requiresNote: boolean;
  autoApply?: boolean;
  active?: boolean;
}

interface TaskListProps {
  parentType: "job" | "leg";
  parentId: string;
  editable?: boolean;
  showHeader?: boolean;
}

interface TaskTemplateGroup {
  _id: string;
  groupKey: string;
  title: string;
  active: boolean;
  appliesTo: "job" | "leg" | "both";
  appType: "trucking" | "serviceRepair" | "all";
}

const ApplyGroupModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  groups: TaskTemplateGroup[];
  isLoading: boolean;
  onApply: (groupKey: string, mode: "dedupe" | "override") => Promise<void>;
}> = ({ visible, onClose, groups, isLoading, onApply }) => {
  const [groupKey, setGroupKey] = useState("");
  const [mode, setMode] = useState<"dedupe" | "override">("dedupe");

  const groupOptions = groups
    .filter((g) => g.active !== false)
    .map((g) => ({
      value: g.groupKey,
      label: `${g.title} (${g.groupKey})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const close = () => {
    setGroupKey("");
    setMode("dedupe");
    onClose();
  };

  return (
    <Modal
      visible={visible}
      setVisible={() => close()}
      title="Apply Group"
      size="md"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Group
          </label>
          <Select
            value={groupKey}
            onChange={(selected: { value: string }) =>
              setGroupKey(selected.value)
            }
            options={groupOptions as any}
            placeholder={isLoading ? "Loading groups..." : "Select a group..."}
            isDisabled={isLoading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Mode
          </label>
          <Select
            value={mode}
            onChange={(selected: { value: "dedupe" | "override" }) =>
              setMode(selected.value)
            }
            options={
              [
                { value: "dedupe", label: "Dedupe (skip existing tasks)" },
                { value: "override", label: "Override (replace group tasks)" },
              ] as any
            }
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="alternative" onClick={close}>
            Cancel
          </Button>
          <Button
            disabled={!groupKey}
            onClick={async () => {
              if (!groupKey) return;
              await onApply(groupKey, mode);
              close();
            }}
          >
            Apply
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Must stay in sync with API enums for Task/TaskTemplate categories
const categoryOptions = [
  { value: "general", label: "General" },
  { value: "pickup", label: "Pickup" },
  { value: "delivery", label: "Delivery" },
  { value: "inspection", label: "Inspection" },
  { value: "safety", label: "Safety" },
  { value: "documentation", label: "Documentation" },
  { value: "customer", label: "Customer" },
];

const industryOptions = [
  { value: "general", label: "General" },
  { value: "trucking", label: "Trucking" },
  { value: "service_repair", label: "Service/Repair" },
] as const;

const appliesToOptions = [
  { value: "both", label: "Jobs & Legs" },
  { value: "job", label: "Jobs Only" },
  { value: "leg", label: "Legs Only" },
] as const;

const appTypeOptions = [
  { value: "all", label: "All" },
  { value: "trucking", label: "Trucking" },
  { value: "serviceRepair", label: "Service/Repair" },
] as const;

const CreateTemplateModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  defaultAppliesTo: "job" | "leg" | "both";
  defaultAppType: "trucking" | "serviceRepair" | "all";
}> = ({ visible, onClose, defaultAppliesTo, defaultAppType }) => {
  const { isSuperAdmin } = usePermissions();
  const canSeeIndustry = isSuperAdmin;
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [industry, setIndustry] = useState<
    "trucking" | "service_repair" | "general"
  >(
    defaultAppType === "trucking"
      ? "trucking"
      : defaultAppType === "serviceRepair"
      ? "service_repair"
      : "general"
  );
  const [appliesTo, setAppliesTo] = useState<"job" | "leg" | "both">(
    defaultAppliesTo
  );
  const [appType, setAppType] = useState<"trucking" | "serviceRepair" | "all">(
    defaultAppType
  );
  const [category, setCategory] = useState("general");
  const [order, setOrder] = useState(0);
  const [required, setRequired] = useState(false);
  const [requiresPhoto, setRequiresPhoto] = useState(false);
  const [requiresSignature, setRequiresSignature] = useState(false);
  const [requiresNote, setRequiresNote] = useState(false);
  const [autoApply, setAutoApply] = useState(false);
  const [active, setActive] = useState(true);

  const [createTemplate, { isLoading }] = useCreateTaskTemplateMutation();

  const reset = () => {
    setName("");
    setTitle("");
    setDescription("");
    setCategory("general");
    setOrder(0);
    setRequired(false);
    setRequiresPhoto(false);
    setRequiresSignature(false);
    setRequiresNote(false);
    setAutoApply(false);
    setActive(true);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Template name is required");
      return;
    }
    if (!title.trim()) {
      toast.error("Task title is required");
      return;
    }
    try {
      const payload: any = {
        name: name.trim(),
        title: title.trim(),
        description: description.trim(),
        appliesTo,
        appType,
        category,
        order,
        required,
        requiresPhoto,
        requiresSignature,
        requiresNote,
        autoApply,
        active,
      };
      if (canSeeIndustry) payload.industry = industry;

      await createTemplate(payload).unwrap();
      toast.success("Template created");
      close();
    } catch (e: any) {
      console.error("Failed to create template:", e);
      toast.error(e?.data?.message || "Failed to create template");
    }
  };

  return (
    <Modal
      visible={visible}
      setVisible={() => close()}
      title="Create Template"
      size="lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template Name *
            </label>
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. pickup_photos"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Task Title *
            </label>
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Take pickup photos"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {!canSeeIndustry ? null : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Industry
              </label>
              <Select
                value={industry}
                onChange={(selected: { value: any }) =>
                  setIndustry(selected.value)
                }
                options={industryOptions as any}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              App Type
            </label>
            <Select
              value={appType}
              onChange={(selected: { value: any }) =>
                setAppType(selected.value)
              }
              options={appTypeOptions as any}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Applies To
            </label>
            <Select
              value={appliesTo}
              onChange={(selected: { value: any }) =>
                setAppliesTo(selected.value)
              }
              options={appliesToOptions as any}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <Select
              value={category}
              onChange={(selected: { value: string }) =>
                setCategory(selected.value)
              }
              options={categoryOptions}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order
            </label>
            <input
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={order}
              onChange={(e) => setOrder(parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="flex items-center gap-2 mt-6">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            <span className="text-sm text-gray-700">Active</span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
            />
            Required
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={requiresPhoto}
              onChange={(e) => setRequiresPhoto(e.target.checked)}
            />
            Requires photo
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={requiresSignature}
              onChange={(e) => setRequiresSignature(e.target.checked)}
            />
            Requires signature
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={requiresNote}
              onChange={(e) => setRequiresNote(e.target.checked)}
            />
            Requires note
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={autoApply}
              onChange={(e) => setAutoApply(e.target.checked)}
            />
            Auto-apply
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="alternative" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={isLoading || !name.trim() || !title.trim()}
          >
            {isLoading ? "Saving..." : "Save Template"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Simple Add Task Modal
const AddTaskModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  onAddCustom: (data: {
    title: string;
    description: string;
    category: string;
    required: boolean;
    requiresPhoto: boolean;
    requiresSignature: boolean;
    requiresNote: boolean;
  }) => void;
  onAddFromTemplate: (template: TaskTemplate) => void;
  templates: TaskTemplate[];
  isLoading: boolean;
  onCreateTemplate: () => void;
}> = ({
  visible,
  onClose,
  onAddCustom,
  onAddFromTemplate,
  templates,
  isLoading,
  onCreateTemplate,
}) => {
  const [mode, setMode] = useState<"template" | "custom">("template");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general");
  const [required, setRequired] = useState(false);
  const [requiresPhoto, setRequiresPhoto] = useState(false);
  const [requiresSignature, setRequiresSignature] = useState(false);
  const [requiresNote, setRequiresNote] = useState(false);

  const handleSubmit = () => {
    if (!title.trim()) return;
    onAddCustom({
      title: title.trim(),
      description: description.trim(),
      category,
      required,
      requiresPhoto,
      requiresSignature,
      requiresNote,
    });
    resetForm();
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setCategory("general");
    setRequired(false);
    setRequiresPhoto(false);
    setRequiresSignature(false);
    setRequiresNote(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      visible={visible}
      setVisible={() => handleClose()}
      title="Add Task"
      size="md"
    >
      <div className="space-y-4">
        {/* Mode Toggle */}
        <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
          <button
            onClick={() => setMode("template")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              mode === "template"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <SparklesIcon className="w-4 h-4 inline mr-1" />
            From Template
          </button>
          <button
            onClick={() => setMode("custom")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              mode === "custom"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <PlusIcon className="w-4 h-4 inline mr-1" />
            Custom Task
          </button>
        </div>

        {mode === "template" ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Pick a reusable template, or create a new one.
              </div>
              <button
                onClick={onCreateTemplate}
                className="text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                Create Template
              </button>
            </div>

            <div className="max-h-80 overflow-y-auto space-y-2">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardDocumentListIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No templates available</p>
                  <button
                    onClick={onCreateTemplate}
                    className="mt-2 text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    Create a template
                  </button>
                  <button
                    onClick={() => setMode("custom")}
                    className="mt-2 text-primary-600 hover:text-primary-700 text-sm"
                  >
                    Create a custom task instead
                  </button>
                </div>
              ) : (
                templates.map((template) => (
                  <button
                    key={template._id}
                    onClick={() => {
                      onAddFromTemplate(template);
                      handleClose();
                    }}
                    className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">
                          {template.title || template.name}
                        </span>
                        {template.required && (
                          <span className="ml-2 text-xs text-red-600">
                            Required
                          </span>
                        )}
                      </div>
                      <PlusIcon className="w-5 h-5 text-gray-400" />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-xs text-gray-500">
              Custom tasks are saved to this {/**/}item only (they are not
              reusable templates).
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter task title..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <Select
                value={category}
                onChange={(selected: { value: string }) =>
                  setCategory(selected.value)
                }
                options={categoryOptions}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Options
              </label>
              <div className="flex flex-wrap gap-2">
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={required}
                    onChange={(e) => setRequired(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Required</span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requiresPhoto}
                    onChange={(e) => setRequiresPhoto(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <CameraIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Photo</span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requiresSignature}
                    onChange={(e) => setRequiresSignature(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <PencilSquareIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Signature</span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requiresNote}
                    onChange={(e) => setRequiresNote(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <DocumentTextIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Note</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="alternative" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!title.trim()}>
                Add Task
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

// Main TaskList Component
export const TaskList: React.FC<TaskListProps> = ({
  parentType,
  parentId,
  editable = true,
  showHeader = true,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showApplyGroupModal, setShowApplyGroupModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);

  const { appType } = useConfig();

  // Queries
  const { data: legTasksData, isLoading: legTasksLoading } =
    useGetLegTasksQuery(parentId, { skip: parentType !== "leg" });

  const { data: jobTasksData, isLoading: jobTasksLoading } =
    useGetJobTasksQuery(parentId, { skip: parentType !== "job" });

  const { data: templates, isLoading: templatesLoading } =
    useGetTaskTemplatesQuery({ appType: appType || "all" });

  const { data: groups, isLoading: groupsLoading } =
    useGetTaskTemplateGroupsQuery(
      { appType: appType || "all" },
      { skip: !editable }
    );

  // Mutations
  const [createLegTask] = useCreateLegTaskMutation();
  const [deleteLegTask] = useDeleteLegTaskMutation();
  const [createTask] = useCreateTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const [applyTemplatesToLeg] = useApplyTemplatesToLegMutation();
  const [applyTemplatesToJob] = useApplyTemplatesToJobMutation();
  const [applyGroupToLeg] = useApplyTemplateGroupToLegMutation();
  const [applyGroupToJob] = useApplyTemplateGroupToJobMutation();

  const tasksData = parentType === "leg" ? legTasksData : jobTasksData;
  const isLoading = parentType === "leg" ? legTasksLoading : jobTasksLoading;

  const tasks: Task[] = tasksData?.tasks || [];
  const stats = tasksData?.stats || {
    total: 0,
    completed: 0,
    pending: 0,
    required: 0,
  };

  const handleAddCustomTask = async (data: {
    title: string;
    description: string;
    category: string;
    required: boolean;
    requiresPhoto: boolean;
    requiresSignature: boolean;
    requiresNote: boolean;
  }) => {
    try {
      if (parentType === "leg") {
        await createLegTask({
          legId: parentId,
          ...data,
        }).unwrap();
      } else {
        await createTask({
          job: parentId,
          parentType: "job",
          ...data,
        }).unwrap();
      }
      toast.success("Task added");
      setShowAddModal(false);
    } catch (error) {
      console.error("Failed to create task:", error);
      toast.error("Failed to add task");
    }
  };

  const handleAddFromTemplate = async (template: TaskTemplate) => {
    try {
      const taskData = {
        title: template.title || template.name,
        description: template.description || "",
        category: template.category,
        required: template.required,
        requiresPhoto: template.requiresPhoto,
        requiresSignature: template.requiresSignature,
        requiresNote: template.requiresNote,
      };

      if (parentType === "leg") {
        await createLegTask({
          legId: parentId,
          ...taskData,
        }).unwrap();
      } else {
        await createTask({
          job: parentId,
          parentType: "job",
          ...taskData,
        }).unwrap();
      }
      toast.success("Task added");
    } catch (error) {
      console.error("Failed to create task from template:", error);
      toast.error("Failed to add task");
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      if (parentType === "leg") {
        await deleteLegTask({
          legId: parentId,
          taskId: taskToDelete._id,
        }).unwrap();
      } else {
        await deleteTask(taskToDelete._id).unwrap();
      }
      toast.success("Task deleted");
      setShowDeleteModal(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error("Failed to delete task:", error);
      toast.error("Failed to delete task");
    }
  };

  const handleApplyTemplates = async () => {
    try {
      if (parentType === "leg") {
        await applyTemplatesToLeg({ legId: parentId, appType }).unwrap();
      } else {
        await applyTemplatesToJob({ jobId: parentId, appType }).unwrap();
      }
      toast.success("Default tasks applied");
    } catch (error) {
      console.error("Failed to apply templates:", error);
      toast.error("Failed to apply templates");
    }
  };

  const handleApplyGroup = async (
    groupKey: string,
    mode: "dedupe" | "override"
  ) => {
    try {
      if (parentType === "leg") {
        await applyGroupToLeg({
          legId: parentId,
          groupKey,
          appType,
          mode,
        }).unwrap();
      } else {
        await applyGroupToJob({
          jobId: parentId,
          groupKey,
          appType,
          mode,
        }).unwrap();
      }
      toast.success("Group applied");
    } catch (error) {
      console.error("Failed to apply group:", error);
      toast.error("Failed to apply group");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
        <span className="ml-2 text-gray-500">Loading tasks...</span>
      </div>
    );
  }

  const completionPercentage =
    stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;

  return (
    <div className="space-y-3">
      {/* Header */}
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardDocumentCheckIcon className="w-5 h-5 text-primary-500" />
            <span className="font-medium text-gray-900">Tasks</span>
            <span className="text-sm text-gray-500">
              {stats.completed}/{stats.total}
            </span>
            {stats.total > 0 && (
              <span className="text-xs text-gray-400">
                ({completionPercentage}%)
              </span>
            )}
            {stats.required > 0 && (
              <span className="text-xs text-red-500 font-medium">
                {stats.required} required
              </span>
            )}
          </div>

          {editable && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowApplyGroupModal(true)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                <Squares2X2Icon className="w-4 h-4 inline mr-1" />
                Apply Group
              </button>
              {tasks.length === 0 && (
                <button
                  onClick={handleApplyTemplates}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  <SparklesIcon className="w-4 h-4 inline mr-1" />
                  Apply Defaults
                </button>
              )}
              <button
                onClick={() => setShowAddModal(true)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                <PlusIcon className="w-4 h-4 inline mr-1" />
                Add
              </button>
            </div>
          )}
        </div>
      )}

      {/* Simple Task List */}
      {tasks.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-sm">
          <p>No tasks</p>
          {editable && (
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-1 text-primary-600 hover:text-primary-700"
            >
              Add a task
            </button>
          )}
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {tasks.map((task) => (
            <div
              key={task._id}
              className="group flex items-center gap-3 py-2 hover:bg-gray-50 -mx-2 px-2 rounded"
            >
              {/* Checkbox (view-only on desktop) */}
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  task.completed
                    ? "bg-green-500 border-green-500 text-white"
                    : "border-gray-300"
                }`}
              >
                {task.completed && <CheckIcon className="w-3 h-3" />}
              </div>

              {/* Title */}
              <span
                className={`flex-1 text-sm ${
                  task.completed
                    ? "text-gray-400 line-through"
                    : "text-gray-900"
                }`}
              >
                {task.title}
              </span>

              {/* Indicators */}
              <div className="flex items-center gap-1">
                {task.required && !task.completed && (
                  <ExclamationCircleIcon className="w-4 h-4 text-red-400" />
                )}
                {task.requiresPhoto && !task.completed && (
                  <CameraIcon className="w-4 h-4 text-gray-300" />
                )}
                {task.requiresSignature && !task.completed && (
                  <PencilSquareIcon className="w-4 h-4 text-gray-300" />
                )}
                {task.requiresNote && !task.completed && (
                  <DocumentTextIcon className="w-4 h-4 text-gray-300" />
                )}
              </div>

              {/* Delete */}
              {editable && (
                <button
                  onClick={() => {
                    setTaskToDelete(task);
                    setShowDeleteModal(true);
                  }}
                  className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Task Modal */}
      <AddTaskModal
        visible={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAddCustom={handleAddCustomTask}
        onAddFromTemplate={handleAddFromTemplate}
        templates={templates || []}
        isLoading={templatesLoading}
        onCreateTemplate={() => setShowCreateTemplateModal(true)}
      />

      <CreateTemplateModal
        visible={showCreateTemplateModal}
        onClose={() => setShowCreateTemplateModal(false)}
        defaultAppliesTo={parentType}
        defaultAppType={(appType as any) || "all"}
      />

      {/* Apply Group Modal */}
      <ApplyGroupModal
        visible={showApplyGroupModal}
        onClose={() => setShowApplyGroupModal(false)}
        groups={(groups || []) as any}
        isLoading={groupsLoading}
        onApply={handleApplyGroup}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        visible={showDeleteModal}
        setVisible={setShowDeleteModal}
        title="Delete Task"
        confirm={handleDeleteTask}
        submitText="Delete"
      >
        <p>
          Are you sure you want to delete "
          <strong>{taskToDelete?.title}</strong>"?
        </p>
      </Modal>
    </div>
  );
};

export default TaskList;
